import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import SwitchInput from '../../../../../components/SwitchInput';
import { CURRENCIES, THEME_COLOR3, THEME_COLOR4 } from '../../../../../constants';
import type { TMenuItemFormValues } from '../schemas/menu-item-schema';

const currencyList = CURRENCIES as Array<{ code: string; symbol: string }>;

type TMenuItemSummaryCardProps = {
  currencyCode: string;
  categoryName?: string;
};

const MenuItemSummaryCard = ({ currencyCode, categoryName = '' }: TMenuItemSummaryCardProps) => {
  const { watch, setValue } = useFormContext<TMenuItemFormValues>();

  const [images, name, priceValue, takeawayPriceValue, enabled, hideThumb] = watch([
    'images',
    'name',
    'priceValue',
    'takeawayPriceValue',
    'enabled',
    'hideMenuThumbnailImages',
  ]);

  const imageUrl = images?.[0] ?? '';
  const currencySymbol = currencyList.find((c) => c.code === currencyCode)?.symbol ?? currencyCode;
  const displayPrice = (priceValue ?? 0).toFixed(2);
  const displayTakeaway = (takeawayPriceValue ?? 0).toFixed(2);

  return (
    <Box sx={styles.card}>
      {/* Image */}
      <Box sx={styles.media}>
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={name || 'Menu item'}
            sx={styles.image}
          />
        ) : (
          <Box sx={styles.imageFallback}>
            {(name || 'M').slice(0, 2).toUpperCase()}
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={styles.content}>
        {categoryName && (
          <Typography sx={styles.category}>{categoryName}</Typography>
        )}
        <Typography sx={styles.title}>{name || 'New Menu Item'}</Typography>

        <Box sx={styles.pricing}>
          <Typography sx={styles.price}>
            {currencySymbol}{displayPrice}
          </Typography>
          <Typography sx={styles.takeaway}>
            Takeaway {currencySymbol}{displayTakeaway}
          </Typography>
        </Box>

        <Box sx={styles.switchRow}>
          <Box sx={styles.switchItem}>
            <SwitchInput
              checked={enabled ?? true}
              onChange={() => setValue('enabled', !enabled, { shouldDirty: true })}
            />
            <Typography sx={styles.switchLabel}>
              {enabled ? 'Enabled' : 'Disabled'}
            </Typography>
          </Box>
          <Box sx={styles.switchItem}>
            <SwitchInput
              checked={!hideThumb}
              onChange={() =>
                setValue('hideMenuThumbnailImages', !hideThumb, { shouldDirty: true })
              }
            />
            <Typography sx={styles.switchLabel}>Show image on menu</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MenuItemSummaryCard;

const styles = {
  card: {
    display: 'flex',
    gap: 2.5,
    alignItems: 'center',
    backgroundColor: '#FFF',
    borderBottom: `1px solid ${THEME_COLOR4}`,
  },
  media: { flexShrink: 0 },
  image: {
    width: 176,
    height: 146,
    borderRadius: '12px',
    objectFit: 'cover',
    border: `1px solid ${THEME_COLOR4}`,
  },
  imageFallback: {
    width: 136,
    height: 112,
    borderRadius: '12px',
    border: `1px solid ${THEME_COLOR4}`,
    backgroundColor: '#FFF',
    color: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'GilroyBold',
    fontSize: 28,
  },
  content: {
    flex: 1,
    minWidth: 0,
    py: { xs: 2, md: 2.5 },
  },
  category: {
    fontFamily: 'GilroyBold',
    fontSize: 11,
    textTransform: 'uppercase',
    color: THEME_COLOR3,
    letterSpacing: '0.08em',
    mb: 0.5,
  },
  title: {
    fontFamily: 'GilroyBold',
    fontSize: { xs: 20, md: 28 },
    color: '#000',
    lineHeight: 1.1,
    mb: 1,
  },
  pricing: { display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'baseline', mb: 1.5 },
  price: { fontFamily: 'GilroyBold', fontSize: 20, color: '#000' },
  takeaway: { fontFamily: 'GilroySemiBold', fontSize: 16, color: THEME_COLOR3 },
  switchRow: { display: 'flex', flexWrap: 'wrap', gap: 2 },
  switchItem: { display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#FFF' },
  switchLabel: { fontFamily: 'GilroyMedium', fontSize: 13, color: '#000' },
};
